import { useQueryClient } from '@tanstack/react-query'
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  type Row,
  useReactTable,
} from '@tanstack/react-table'
import {
  AlertTriangle,
  Copy,
  ExternalLink,
  Power,
  PowerOff,
  RefreshCw,
} from 'lucide-react'
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import CopyButtonWithAnimation from 'components/reusable/CopyButtonWithAnimation'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { Badge } from 'slab'
import { getLocaleDateString } from 'utils'
import {
  getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey,
  useGetBrokersMembersMemberIdStripeIssuingCards,
  usePostBrokersMembersMemberIdStripeIssuingCardsCardIdActivate,
  usePostBrokersMembersMemberIdStripeIssuingCardsCardIdDeactivate,
  usePostBrokersMembersMemberIdStripeIssuingCardsCardIdSyncStatus,
} from 'utils/web_api'
import { type BrokersStripeIssuingCard } from 'utils/web_api.schemas'

type StripeCardsTableProps = {
  memberId: string
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

const columnHelper = createColumnHelper<BrokersStripeIssuingCard>()

const StatusBadge = ({ status }: { status: string }) => {
  switch (status) {
    case 'active':
      return <Badge type='success'>Active</Badge>
    case 'inactive':
      return <Badge type='neutral'>Inactive</Badge>
    case 'canceled':
      return <Badge type='critical'>Canceled</Badge>
    case 'blocked':
      return <Badge type='critical'>Blocked</Badge>
    default:
      return <Badge type='neutral'>{status}</Badge>
  }
}

const StripeCardActions = ({
  memberId,
  card,
  pagination,
}: {
  memberId: string
  card: BrokersStripeIssuingCard
  pagination: PaginationState
}) => {
  const queryClient = useQueryClient()

  const queryKey = getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey(
    memberId,
    {
      'page[number]': pagination.pageIndex,
      'page[size]': pagination.pageSize,
    }
  )

  const updateCardInCache = (updatedCard: BrokersStripeIssuingCard) => {
    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData

      return {
        ...oldData,
        data: oldData.data.map((existingCard: BrokersStripeIssuingCard) =>
          existingCard.id === updatedCard.id ? updatedCard : existingCard
        ),
      }
    })
  }

  const syncCardStatusMutation =
    usePostBrokersMembersMemberIdStripeIssuingCardsCardIdSyncStatus({
      mutation: {
        onSuccess: (data) => {
          if (data) {
            updateCardInCache(data)
          }
        },
      },
    })

  const activateCardMutation =
    usePostBrokersMembersMemberIdStripeIssuingCardsCardIdActivate({
      mutation: {
        onSuccess: (data) => {
          if (data) {
            updateCardInCache(data)
          }
        },
      },
    })

  const deactivateCardMutation =
    usePostBrokersMembersMemberIdStripeIssuingCardsCardIdDeactivate({
      mutation: {
        onSuccess: (data) => {
          if (data) {
            updateCardInCache(data)
          }
        },
      },
    })

  const handleSyncStatus = async () => {
    try {
      await syncCardStatusMutation.mutateAsync({
        memberId,
        cardId: card.id,
      })
      // The mutation should automatically refetch the data due to query invalidation
    } catch (error) {
      console.error('Failed to sync card status:', error)
    }
  }

  const handleActivate = async () => {
    try {
      await activateCardMutation.mutateAsync({
        memberId,
        cardId: card.id,
      })
    } catch (error) {
      console.error('Failed to activate card:', error)
    }
  }

  const handleDeactivate = async () => {
    try {
      await deactivateCardMutation.mutateAsync({
        memberId,
        cardId: card.id,
      })
    } catch (error) {
      console.error('Failed to deactivate card:', error)
    }
  }

  return (
    <div className='flex items-center gap-4'>
      <DisplayTooltipOnHover text='Sync card status with Stripe'>
        <div className='cursor-pointer h-full' onClick={handleSyncStatus}>
          <RefreshCw
            size={20}
            className={`stroke-current text-gray-600 ${
              syncCardStatusMutation.isPending ? 'animate-spin' : ''
            }`}
          />
        </div>
      </DisplayTooltipOnHover>

      {card.remote_status === 'active'
        ? (
          <DisplayTooltipOnHover text='Deactivate card'>
            <div className='cursor-pointer h-full' onClick={handleDeactivate}>
              <PowerOff
                size={20}
                className={`stroke-current text-red-600 ${
                deactivateCardMutation.isPending ? 'opacity-50' : ''
              }`}
              />
            </div>
          </DisplayTooltipOnHover>
          )
        : (
          <DisplayTooltipOnHover text='Activate card'>
            <div className='cursor-pointer h-full' onClick={handleActivate}>
              <Power
                size={20}
                className={`stroke-current text-green-600 ${
                activateCardMutation.isPending ? 'opacity-50' : ''
              }`}
              />
            </div>
          </DisplayTooltipOnHover>
          )}

      <DisplayTooltipOnHover text='View card in Stripe dashboard'>
        <div
          className='cursor-pointer h-full'
          onClick={(e) => {
            e.preventDefault()
            window.open(
              card.view_card_on_stripe_url,
              '_blank',
              'noopener noreferrer'
            )
          }}
        >
          <ExternalLink size={20} className='stroke-current text-gray-600' />
        </div>
      </DisplayTooltipOnHover>
    </div>
  )
}

const StripeCardsTable = ({
  memberId,
  pagination,
  setPagination,
}: StripeCardsTableProps) => {
  const { data, isLoading, isError, error } =
    useGetBrokersMembersMemberIdStripeIssuingCards(memberId, {
      'page[number]': pagination.pageIndex,
      'page[size]': pagination.pageSize,
    }, {
      query: {
        enabled: !!memberId,
        refetchOnWindowFocus: false,
      },
    })

  const [paginationMetadata, setPaginationMetadata] = useState<{
    total_records: number
    current_page: number
    total_pages: number
    next_page: number | null
    prev_page: number | null
  }>()

  const columns = useMemo(
    () => [
      columnHelper.display({
        header: 'Actions',
        cell: ({ row }: { row: Row<BrokersStripeIssuingCard> }) => (
          <StripeCardActions
            memberId={memberId}
            card={row.original}
            pagination={pagination}
          />
        ),
      }),
      columnHelper.accessor('last4', {
        header: 'Last 4',
        cell: (info) => <Badge type='info'>{info.getValue()}</Badge>,
      }),
      columnHelper.accessor('remote_status', {
        header: 'Status',
        cell: (info) => <StatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor('remote_id', {
        header: 'Stripe ID',
        cell: (info) => {
          const fullId = info.getValue()
          const last4 = fullId.slice(-4)
          const truncatedId = `ic_...${last4}`

          return (
            <div className='flex items-center'>
              <code className='text-sm bg-gray-100 px-2 py-1 rounded'>
                {truncatedId}
              </code>
              <CopyButtonWithAnimation content={fullId}>
                <Copy size={16} className='text-gray-600' />
              </CopyButtonWithAnimation>
            </div>
          )
        },
      }),
      columnHelper.display({
        header: 'Enrollments',
        cell: (info) => {
          const card = info.row.original
          if (!card.enrollments?.length && card.remote_status === 'active') {
            return (
              <DisplayTooltipOnHover text='This card is not mapped to any enrollments. Deactivate it to prevent future charges.'>
                <Badge type='critical' leftIcon={AlertTriangle}>
                  Deactivate card!
                </Badge>
              </DisplayTooltipOnHover>
            )
          }

          return (
            <div className='flex gap-1 items-center'>
              {card.enrollments?.map((enrollment) => (
                <Badge key={enrollment.id} type='info'>
                  {enrollment.id.slice(-6)}
                </Badge>
              ))}
            </div>
          )
        },
      }),
      columnHelper.accessor('created_at', {
        header: 'Created',
        cell: (info) => getLocaleDateString(new Date(info.getValue())),
      }),
      columnHelper.accessor('updated_at', {
        header: 'Last Updated',
        cell: (info) => getLocaleDateString(new Date(info.getValue())),
      }),
    ],
    [memberId, pagination]
  )

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    state: {
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data.pagination)
    }
  }, [data?.pagination])

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      errorMessage={(error as GrapeAPIErrorType)?.errors[0]}
      noDataMessage='No Stripe issuing cards found for this member.'
      paginationLabelSingular='card'
      paginationMetadata={paginationMetadata}
      tableMessageHeightClass='h-32'
      onNextPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.next_page || 0,
        })}
      onPreviousPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.prev_page || 0,
        })}
    />
  )
}

export default StripeCardsTable
